import { intersection } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Input } from 'reactstrap';

import CategorySelect from '../../generic/CategorySelect';
import MultiSelect from '../../generic/MultiSelect';
import SingleSelect from '../../generic/SingleSelect';
import convertToOption from '../../utils/convert-to-option';
import {
  countryOptions,
  genderOptions,
  imageTypeOptions,
  originOptions,
} from '../constants';
import CreativeCompatibilityActions from './CreativeCompatibilityActions';

function getProductNames(products) {
  return products.map(p => p.name).join(', ');
}

const getCommonSelectProps = (accessor, width) => ({
  styles: {
    input: base => ({ ...base, width }),
    menuPortal: base => ({ ...base, zIndex: 1051 }),
  },
  className: `m-select--small qa-field-${accessor}`,
  // Override select styles to make it display correctly while shown
  // from within a modal: https://react-select.com/advanced#portaling
  menuPortalTarget: document.body,
});

const getInputId = (row, accessor) => `${row.original.id}.${accessor}`;

const getCountries = compatibleCountries => {
  if (compatibleCountries && compatibleCountries.length > 0) {
    return countryOptions.filter(country =>
      compatibleCountries.includes(country.value),
    );
  }
  return countryOptions;
};

const getProductTagOptions = (
  campaignProductTags,
  compatibleProductTags,
  compatibleProducts,
) =>
  compatibleProducts.length
    ? intersection(campaignProductTags, compatibleProductTags).map(
        convertToOption,
      )
    : campaignProductTags.map(convertToOption);

export const nameColumn = onChange => {
  const accessor = 'name';
  return {
    Header: 'Name',
    accessor,
    Cell: row => (
      <Input
        type="textarea"
        name={getInputId(row, accessor)}
        className="form-control--small"
        defaultValue={row.value}
        title={row.value}
        onBlur={onChange}
        style={{ height: '100%' }}
        data-qa-field={accessor}
      />
    ),
    width: 140,
    className: 'text-wrap',
  };
};

export const origin = onChange => {
  const accessor = 'origin';
  return {
    Header: 'Origin',
    accessor,
    width: 132,
    Cell: row => (
      <SingleSelect
        isSearchable={false}
        {...getCommonSelectProps(accessor, 75)}
        options={originOptions}
        name={getInputId(row, accessor)}
        value={row.value}
        onChange={onChange}
        defaultValue={originOptions[0]}
        id={getInputId(row, accessor)}
      />
    ),
  };
};

export const mediaSet = onChange => {
  const accessor = 'mediaSet';
  return {
    Header: 'Media Set',
    accessor,
    Cell: row => (
      <Input
        name={getInputId(row, accessor)}
        defaultValue={row.value}
        onBlur={onChange}
        className="form-control--small"
        data-qa-field={accessor}
      />
    ),
    width: 65,
  };
};

export const imageType = onChange => {
  const accessor = 'imageType';
  return {
    Header: 'Type',
    accessor,
    className: 'text-wrap',
    width: 154,
    Cell: row => (
      <MultiSelect
        isSearchable={false}
        {...getCommonSelectProps(accessor, 95)}
        options={imageTypeOptions}
        name={getInputId(row, accessor)}
        value={row.value}
        onChange={onChange}
        defaultValue={[]}
        id={getInputId(row, accessor)}
      />
    ),
  };
};

export const genders = onChange => {
  const accessor = 'genders';
  return {
    Header: 'Genders *',
    accessor,
    width: 125,
    filterMethod: (filter, value) =>
      value.genders?.join().toLowerCase() === filter.value.toLowerCase(),
    Cell: row => (
      <MultiSelect
        isSearchable={false}
        {...getCommonSelectProps(accessor, 80)}
        options={genderOptions}
        name={getInputId(row, accessor)}
        value={row.value}
        onChange={onChange}
        id={getInputId(row, accessor)}
      />
    ),
    className: 'text-wrap',
  };
};

export const productTags = onChange => {
  const accessor = 'productTags';
  return {
    Header: 'Product Tags',
    accessor,
    width: 140,
    Cell: row => (
      <MultiSelect
        isSearchable={false}
        {...getCommonSelectProps(accessor, 80)}
        options={getProductTagOptions(
          row.original.campaignProductTags,
          row.original.compatibleProductTags,
          row.original.products.compatibleProducts,
        )}
        name={getInputId(row, accessor)}
        value={row.value}
        onChange={onChange}
        id={getInputId(row, accessor)}
      />
    ),
    className: 'text-wrap',
  };
};

export const countries = onChange => {
  const accessor = 'countryCodes';
  return {
    Header: 'Countries',
    accessor,
    width: 105,
    Cell: row => (
      <MultiSelect
        isSearchable
        {...getCommonSelectProps(accessor, 80)}
        options={getCountries(row.original.compatibleCountries)}
        name={getInputId(row, accessor)}
        value={row.value}
        onChange={onChange}
        id={getInputId(row, accessor)}
      />
    ),
    className: 'text-wrap',
  };
};

export const categories = onChange => {
  const accessor = 'categories';
  return {
    Header: 'Categories',
    accessor,
    width: 165,
    Cell: row => (
      <CategorySelect
        {...getCommonSelectProps(accessor, 80)}
        name={getInputId(row, accessor)}
        value={row.value}
        onChange={onChange}
        id={getInputId(row, accessor)}
        defaultValue={[]}
        filteredValues={row.original.compatibleCategories}
      />
    ),
    className: 'text-wrap',
  };
};

export const compatibleProducts =
  (
    campaignRelatedProducts = true,
    onSuccess,
    onDeleteCreative,
    onResizeCreativeImage,
  ) =>
  (onChange, campaignId) => ({
    Header: `Compatibility (${campaignRelatedProducts ? 'Campaign' : 'All'})`,
    accessor: `products.${
      campaignRelatedProducts ? 'compatibleProducts' : 'allCompatibleProducts'
    }`,
    sortMethod: (a, b) => {
      const names1 = getProductNames(a);
      const names2 = getProductNames(b);
      if (names1 === names2) {
        return 0;
      }
      return names1 > names2 ? 1 : -1;
    },
    filterMethod: ({ value, id }, row) =>
      row[id].find(({ name }) =>
        name.toLowerCase().includes(value.toLowerCase()),
      ),
    width: 148,
    Cell: row => {
      const [isLoading, setIsLoading] = useState(false);

      useEffect(() => {
        if (isLoading) {
          onResizeCreativeImage(row.original.id).then(() =>
            setIsLoading(false),
          );
        }
      }, [row.original.id, isLoading]);

      const resizeAndSetLoading = () => {
        setIsLoading(true);
      };

      const handleChange = (event, id) => {
        let productIds = [...row.original.productIds];

        if (event.target.checked) {
          productIds.push(id);
        } else {
          productIds = productIds.filter(productId => {
            return productId !== id;
          });
        }
        onChange({
          target: {
            name: getInputId(row, 'productIds'),
            value: productIds,
          },
        });
        return true;
      };

      return row.value ? (
        <CreativeCompatibilityActions
          handleChange={handleChange}
          resizeAndSetLoading={resizeAndSetLoading}
          campaignRelatedProducts={campaignRelatedProducts}
          onSuccess={onSuccess}
          onDeleteCreative={onDeleteCreative}
          isLoading={isLoading}
          campaignId={campaignId}
          creative={row.original}
          products={row.value}
        />
      ) : (
        '-'
      );
    },
  });
